import { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import fetchCoreFeeScheduleProgram from 'src/actions/Group/Network/fetchCoreFeeScheduleProgram';
import {
  caseTypeOptions,
  isDefaultProgramSelected,
  isValidServiceType,
  networkOptions,
} from 'src/components/Cases/utils';

const normalizeSearch = (search) => (search || '').trim();

const getGroupProgram = (groupsPrograms, selectedProgramId) => (
  _.find(
    _.get(groupsPrograms, 'data', []),
    (program) => program.id === selectedProgramId,
  )
);

const fetchPrimaryWorkersForProgram = ({
  nextSelectedProgramId,
  oonProgramId,
  groupId,
  fetchProvidersUserCore,
  fetchGroupsUsersFromProgram,
}) => (
  nextSelectedProgramId === oonProgramId ?
    fetchProvidersUserCore({ providers: groupId, options: { size: 50, licensed: false } }) :
    fetchGroupsUsersFromProgram(groupId, nextSelectedProgramId)
);

export const useAddCaseDetailsProgramFlow = ({
  assistanceRequest,
  canPaginateNetworkGroups,
  defaultProgram,
  fetchCoreGroupForms,
  fetchProvidersUserCore,
  fetchGroupsUsersFromProgram,
  group,
  groupId,
  groupsPrograms,
  hasNetworkLicense,
  networks,
  oonProgram,
  paginateSearchNetworkGroups,
  searchOONGroups,
  shouldInitializeForm,
  user,
  buildLegacyFields,
}) => {
  const [defaultProgramSelected, setDefaultProgramSelected] = useState(false);
  const [invalidServiceType, setInvalidServiceType] = useState(null);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [feeScheduleProgramId, setFeeScheduleProgramId] = useState('');
  const [feeScheduleProgram, setFeeScheduleProgram] = useState(null);
  const [primaryWorkersReady, setPrimaryWorkersReady] = useState(false);

  const fetchGroupsUsers = useCallback((search) => {
    const oonProgramId = _.get(oonProgram, 'id', '');
    const normalizedSearch = normalizeSearch(search);
    const hasSearchText = normalizedSearch.length > 0;

    if (selectedProgramId === oonProgramId) {
      const options = {
        page: 1,
        size: 50,
        licensed: false,
      };

      if (hasSearchText) {
        options.text = normalizedSearch;
      }

      fetchProvidersUserCore({
        providers: groupId,
        options,
      });
      return;
    }

    if (hasSearchText) {
      fetchGroupsUsersFromProgram(groupId, selectedProgramId, { text: normalizedSearch, per: 50, page: 1 });
    } else {
      fetchGroupsUsersFromProgram(groupId, selectedProgramId);
    }
  }, [fetchProvidersUserCore, fetchGroupsUsersFromProgram, groupId, oonProgram, selectedProgramId]);

  const debouncedFetchGroupsUsers = useMemo(
    () => _.debounce(fetchGroupsUsers, 400),
    [fetchGroupsUsers],
  );

  useEffect(() => () => debouncedFetchGroupsUsers.cancel(), [debouncedFetchGroupsUsers]);

  const initializeProgramState = useCallback((values) => {
    const selectedProgramValue = values?.program?.id;
    if (!selectedProgramValue) return;

    const groupProgram = getGroupProgram(groupsPrograms, selectedProgramValue);
    const nextFeeScheduleProgramId = groupProgram?.relationships.fee_schedule_program?.data?.id;
    const isDefault = isDefaultProgramSelected(groupsPrograms, values, defaultProgram);

    setDefaultProgramSelected(isDefault);
    setFeeScheduleProgramId(nextFeeScheduleProgramId || '');
    if (!nextFeeScheduleProgramId) {
      setFeeScheduleProgram(null);
    }
  }, [defaultProgram, groupsPrograms]);

  useEffect(() => {
    if (feeScheduleProgramId && !feeScheduleProgram) {
      fetchCoreFeeScheduleProgram(feeScheduleProgramId)
        .then((fsp) => setFeeScheduleProgram(fsp))
        .catch(console.error);
    }
  }, [feeScheduleProgram, feeScheduleProgramId]);

  const onCaseTypeChange = useCallback((form) => {
    const caseTypeOptionsLength = caseTypeOptions(group.name, hasNetworkLicense).length;

    if (caseTypeOptionsLength > 1) {
      form.batch(() => {
        form.change('network', undefined);
        form.change('service_case.primary_worker_id', undefined);
        form.change('program', undefined);
        form.change('service_case.service_type', undefined);
        form.resetFieldState('network');
        form.resetFieldState('service_case.primary_worker_id');
        form.resetFieldState('program');
        form.resetFieldState('service_case.service_type');
      });
    }
  }, [group.name, hasNetworkLicense]);

  const onNetworkChange = useCallback((form, values) => {
    if (networkOptions(networks).length > 1 && values?.service_case?.service_type) {
      form.batch(() => {
        form.change('service_case.service_type', undefined);
        form.resetFieldState('service_case.service_type');
      });
    }
  }, [networks]);

  const onServiceTypeChange = useCallback((serviceType, _form, _values, legacyFields) => {
    if (serviceType?.id) {
      fetchCoreGroupForms(groupId, [serviceType.id]);
    }

    if (defaultProgramSelected) {
      if (canPaginateNetworkGroups) {
        paginateSearchNetworkGroups(legacyFields);
      } else {
        searchOONGroups(legacyFields);
      }
    }
  }, [
    canPaginateNetworkGroups,
    defaultProgramSelected,
    fetchCoreGroupForms,
    groupId,
    paginateSearchNetworkGroups,
    searchOONGroups,
  ]);

  const onProgramChange = useCallback((selectedProgram, form, values) => {
    form.batch(() => {
      form.change('service_case.primary_worker_id', undefined);
      form.change('service_case.service_type', undefined);
      form.resetFieldState('service_case.primary_worker_id');
      form.resetFieldState('service_case.service_type');
    });

    const nextSelectedProgramId = selectedProgram?.id || '';
    setSelectedProgramId(nextSelectedProgramId);

    const groupProgram = getGroupProgram(groupsPrograms, nextSelectedProgramId);
    const nextFeeScheduleProgramId = groupProgram?.relationships.fee_schedule_program?.data?.id;
    setFeeScheduleProgramId(nextFeeScheduleProgramId || '');

    if (nextFeeScheduleProgramId) {
      fetchCoreFeeScheduleProgram(nextFeeScheduleProgramId)
        .then((value) => setFeeScheduleProgram(value))
        .catch(console.error);
    } else {
      setFeeScheduleProgram(null);
    }

    if (!shouldInitializeForm) {
      setDefaultProgramSelected(_.get(groupProgram, 'attributes.default', false));
    }

    if (!selectedProgram) {
      setPrimaryWorkersReady(false);
      return;
    }

    const oonProgramId = _.get(oonProgram, 'id', '');
    const arServiceType = _.get(assistanceRequest, 'service_type');

    setPrimaryWorkersReady(false);

    if (!_.isEmpty(arServiceType)) {
      if (isValidServiceType({
        values,
        groupProgram,
        serviceType: arServiceType,
        user,
      })) {
        form.change('service_case.service_type', arServiceType);
        const legacyFields = buildLegacyFields(form, values);
        if (legacyFields?.service_case?.service_type) {
          legacyFields.service_case.service_type.value = arServiceType;
        }
        onServiceTypeChange(arServiceType, form, values, legacyFields);
      } else {
        setInvalidServiceType(arServiceType);
        form.blur('service_case.service_type');
      }
    }

    const fetchPromise = fetchPrimaryWorkersForProgram({
      nextSelectedProgramId,
      oonProgramId,
      groupId,
      fetchProvidersUserCore,
      fetchGroupsUsersFromProgram,
    });

    Promise.resolve(fetchPromise)
      .catch(console.error)
      .finally(() => setPrimaryWorkersReady(true));
  }, [
    assistanceRequest,
    buildLegacyFields,
    fetchGroupsUsersFromProgram,
    fetchProvidersUserCore,
    groupId,
    groupsPrograms,
    oonProgram,
    onServiceTypeChange,
    shouldInitializeForm,
    user,
  ]);

  return {
    debouncedFetchGroupsUsers,
    defaultProgramSelected,
    feeScheduleProgram,
    feeScheduleProgramId,
    initializeProgramState,
    invalidServiceType,
    onCaseTypeChange,
    onNetworkChange,
    onProgramChange,
    onServiceTypeChange,
    primaryWorkersReady,
    selectedProgramId,
  };
};
